import React, { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";
import { useCart } from "@/context/CartContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents, currency) => `${currency === "eur" ? "€" : "$"}${(cents / 100).toFixed(2)}`;

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const orderId = params.get("order_id") || params.get("session_id");
  const { clearCart } = useCart();
  const [state, setState] = useState({ loading: true, data: null, error: null });
  const attempts = useRef(0);

  useEffect(() => {
    if (!orderId) {
      setState({ loading: false, data: null, error: "Missing order id" });
      return;
    }
    let timer;
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/payments/status/${orderId}`);
        if (data.payment_status === "paid") {
          setState({ loading: false, data, error: null });
          clearCart();
          return;
        }
        attempts.current += 1;
        if (attempts.current < 15) {
          timer = setTimeout(poll, 2000);
        } else {
          setState({ loading: false, data, error: "Payment is still processing. Check your email for confirmation." });
        }
      } catch (e) {
        setState({ loading: false, data: null, error: e.response?.data?.detail || "Could not verify payment" });
      }
    };
    poll();
    return () => clearTimeout(timer);
  }, [orderId, clearCart]);

  return (
    <div data-testid="payment-success-page" className="min-h-screen bg-neutral-950 text-neutral-100 grid-pattern flex items-center justify-center px-4">
      <div className="max-w-lg w-full bg-neutral-900 border border-neutral-800 rounded p-10 text-center space-y-6">
        {state.loading && (
          <>
            <Loader2 className="w-12 h-12 text-white animate-spin mx-auto" />
            <h1 className="font-disp text-3xl uppercase text-white">Confirming Payment…</h1>
            <p className="text-sm text-neutral-400">Hold tight while we verify your order.</p>
          </>
        )}
        {!state.loading && state.data?.payment_status === "paid" && (
          <>
            <CheckCircle2 data-testid="payment-success-icon" className="w-12 h-12 text-white mx-auto" />
            <h1 className="font-disp text-4xl uppercase text-white">Order Locked In</h1>
            <p className="text-sm text-neutral-300">
              Payment confirmed. Your custom build goes into production — we'll cut it to spec and ship it tracked, worldwide.
            </p>
            {state.data.order && (
              <div data-testid="order-summary" className="text-left border border-neutral-800 rounded divide-y divide-neutral-800">
                {state.data.order.items.map((item) => (
                  <div key={item.product_id} className="flex items-center justify-between px-4 py-3 text-sm">
                    <span className="text-neutral-200">
                      {item.name} <span className="text-neutral-500 font-mono">×{item.quantity}</span>
                    </span>
                    <span className="font-mono text-white">{fmt(item.unit_amount * item.quantity, state.data.order.currency)}</span>
                  </div>
                ))}
                <div className="flex items-center justify-between px-4 py-3">
                  <span className="text-xs font-mono uppercase text-neutral-400">Total Charged</span>
                  <span data-testid="order-total" className="font-disp text-xl text-white">{fmt(state.data.order.amount, state.data.order.currency)}</span>
                </div>
              </div>
            )}
            {state.data.order?.custom_details && (
              <p className="text-xs font-mono text-neutral-400">
                Build notes: <span className="text-neutral-200">{state.data.order.custom_details}</span>
              </p>
            )}
            {state.data.order && (
              <div className="bg-neutral-950 border border-neutral-800 rounded p-4 space-y-2">
                <p className="text-xs font-mono text-neutral-400">Save your order number:</p>
                <p data-testid="order-id-display" className="font-mono text-sm text-white break-all select-all">{state.data.order.order_id}</p>
                <Link
                  data-testid="track-order-link"
                  to="/track"
                  className="inline-block text-xs font-bold uppercase tracking-wider text-white underline underline-offset-4 hover:text-white transition-colors"
                >
                  Track this order →
                </Link>
              </div>
            )}
            <Link
              data-testid="back-to-store-link"
              to="/"
              className="inline-block px-8 py-3 bg-white hover:bg-neutral-200 text-black font-disp uppercase tracking-wider rounded transition-colors"
            >
              Back to the Shop
            </Link>
          </>
        )}
        {!state.loading && state.error && (
          <>
            <XCircle data-testid="payment-error-icon" className="w-12 h-12 text-red-400 mx-auto" />
            <h1 className="font-disp text-3xl uppercase text-white">Payment Status Unknown</h1>
            <p data-testid="payment-error-message" className="text-sm text-neutral-400">{state.error}</p>
            <Link
              data-testid="back-to-store-link"
              to="/"
              className="inline-block px-8 py-3 bg-neutral-800 hover:bg-neutral-700 text-white font-disp uppercase tracking-wider rounded border border-neutral-700 transition-colors"
            >
              Back to the Shop
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
